import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import { useState, useCallback, useMemo } from "react";
import { View, Text, ScrollView, Pressable, Platform } from "react-native";
import {
  Droplets,
  Scan,
  Dna,
  FlaskConical,
  Activity,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  getBlocksByKind,
  type ContentBlock,
  type TestMarkerCategory,
} from "@src/packages/content";
import { CommonActions } from "@react-navigation/native";
import { useTranslations, useLocale } from "@src/packages/i18n";
import { EMBER } from "../../../onboarding/theme";
import type { ChatStackParamList } from "../../types";
import { getMetricFactsheets } from "../../data/metricFactsheets";
import {
  MoonFilterChip,
  MoonSearchInput,
  BackLink,
  cardSurface,
} from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import { useSmartBack, getBackLabel } from "../../hooks/useSmartBack";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";
import { colors } from "@louise/theme/tokens";

/* ─── Category config ────────────────────────────────────────── */

const CATEGORY_CONFIG: {
  key: TestMarkerCategory;
  i18nKey: string;
  icon: LucideIcon;
}[] = [
  { key: "blood-marker", i18nKey: "blood_marker", icon: Droplets },
  { key: "imaging", i18nKey: "imaging", icon: Scan },
  { key: "genetic", i18nKey: "genetic", icon: Dna },
  { key: "semen", i18nKey: "semen", icon: FlaskConical },
  { key: "monitoring", i18nKey: "monitoring", icon: Activity },
];

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  "blood-marker": Droplets,
  imaging: Scan,
  genetic: Dna,
  semen: FlaskConical,
  monitoring: Activity,
};

type Props = NativeStackScreenProps<ChatStackParamList, "Tests">;

type TestCatalogItem =
  | {
      type: "test";
      id: string;
      title: string;
      subtitle: string;
      detail?: string;
      category?: TestMarkerCategory;
      keywords: string;
      block: ContentBlock<"test">;
    }
  | {
      type: "metric";
      id: string;
      title: string;
      subtitle: string;
      detail?: string;
      category: TestMarkerCategory;
      keywords: string;
      metricKey: string;
    };

export default function TestsScreen({ route, navigation }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("TestsScreen");
  const { locale } = useLocale();
  const returnTab = route.params?.returnTab;
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Learn",
    fallbackScreen: "Learn",
  });
  const backLabel =
    returnTab && returnTab !== "Learn"
      ? getBackLabel(returnTab, "Learn", t("you.shared.back"))
      : t("learn.back_to_learn");
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] =
    useState<TestMarkerCategory | null>(null);
  const tests = useMemo(() => {
    void locale;
    return getBlocksByKind("test");
  }, [locale]);
  const metricFactsheets = useMemo(() => getMetricFactsheets(locale), [locale]);
  const catalogItems = useMemo<TestCatalogItem[]>(() => {
    const testItems: TestCatalogItem[] = tests.map((test) => ({
      type: "test",
      id: test.id,
      title: test.title,
      subtitle: test.subtitle ?? test.body.whatItMeasures,
      detail: test.body.turnaroundTime,
      category: test.body.category,
      keywords: [
        test.title,
        test.subtitle,
        test.body.testName,
        test.body.whatItMeasures,
        test.tags.join(" "),
      ].join(" "),
      block: test,
    }));

    const metricItems: TestCatalogItem[] = metricFactsheets.map(
      ({ key, metric, keywords }) => ({
        type: "metric",
        id: `metric-${key}`,
        title: metric.label,
        subtitle: metric.description,
        detail:
          metric.valueMode === "cycle"
            ? t("learn.encyclopedia_screens.tests.categories.monitoring")
            : metric.normalRange,
        category: "monitoring",
        keywords: [
          metric.label,
          metric.description,
          metric.whyItMatters,
          metric.normalRange,
          keywords,
        ].join(" "),
        metricKey: key,
      }),
    );

    return [...testItems, ...metricItems];
  }, [tests, metricFactsheets, t]);

  const filtered = useMemo(() => {
    let result = catalogItems;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          item.subtitle.toLowerCase().includes(q) ||
          item.keywords.toLowerCase().includes(q),
      );
    }

    if (activeCategory) {
      result = result.filter((item) => item.category === activeCategory);
    }

    result.sort((a, b) => a.title.localeCompare(b.title));
    return result;
  }, [catalogItems, search, activeCategory]);

  const onItemPress = useCallback(
    (item: TestCatalogItem) => {
      // Catalogue/metric labels are authored copy, not the patient's results.
      capturePress(
        pressLabel(item.title),
        item.type === "metric" ? "learn_metric_card" : "learn_test_card",
      );
      if (item.type === "metric") {
        navigation.dispatch(
          CommonActions.navigate("Care", {
            screen: "MetricDetail",
            params: { metricKey: item.metricKey },
          }),
        );
        return;
      }

      navigation.navigate("TestDetail", { testId: item.block.id });
    },
    [capturePress, navigation],
  );

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <BackLink
          label={backLabel}
          action="learn_encyclopedia_back"
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 38,
            fontWeight: "700",
            color: colors.deepBrown,
            lineHeight: 42,
            letterSpacing: -0.5,
            ...(Platform.OS === "web" && {
              ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }),
          }}
        >
          {t("learn.encyclopedia_screens.tests.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 21,
          }}
        >
          {t("learn.encyclopedia_screens.tests.subtitle")}
        </Text>

        {/* Search bar */}
        <MoonSearchInput
          value={search}
          onChangeText={setSearch}
          placeholder={t("learn.encyclopedia_screens.tests.search_placeholder")}
          onClear={() => setSearch("")}
          tone="glass"
          style={{ marginTop: 18 }}
        />

        {/* Category filter chips */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 8,
            paddingVertical: 14,
          }}
        >
          <MoonFilterChip
            label={t("learn.encyclopedia_screens.all")}
            selected={!activeCategory}
            onPress={() => setActiveCategory(null)}
            tone="glass"
            showCheck={false}
          />
          {CATEGORY_CONFIG.map(({ key, i18nKey, icon: Icon }) => {
            const isActive = activeCategory === key;
            return (
              <MoonFilterChip
                key={key}
                label={t(
                  `learn.encyclopedia_screens.tests.categories.${i18nKey}`,
                )}
                icon={Icon}
                selected={isActive}
                onPress={() => setActiveCategory(isActive ? null : key)}
                tone="glass"
                showCheck={false}
              />
            );
          })}
        </ScrollView>

        {/* Result count */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.textFaint,
            marginBottom: 14,
          }}
        >
          {t(
            filtered.length === 1
              ? "learn.encyclopedia_screens.tests.count_one"
              : "learn.encyclopedia_screens.tests.count_other",
            { params: { count: String(filtered.length) } },
          )}
        </Text>

        {/* Flat list items */}
        <View style={{ gap: 8 }}>
          {filtered.map((item) => {
            const CatIcon = CATEGORY_ICONS[item.category ?? ""] ?? FlaskConical;
            return (
              <Pressable
                key={item.id}
                onPress={() => onItemPress(item)}
                style={{
                  ...cardSurface(),
                  flexDirection: "row" as const,
                  alignItems: "center" as const,
                  paddingHorizontal: 16,
                  paddingVertical: 16,
                  gap: 14,
                }}
              >
                <CatIcon
                  size={22}
                  color={EMBER.colors.coral}
                  strokeWidth={1.5}
                />

                <View style={{ flex: 1, gap: 2 }}>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 15,
                      fontWeight: "600",
                      color: EMBER.colors.text,
                      lineHeight: 20,
                    }}
                    numberOfLines={1}
                  >
                    {item.title}
                  </Text>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      color: EMBER.colors.textLight,
                      lineHeight: 18,
                    }}
                    numberOfLines={2}
                  >
                    {item.subtitle}
                  </Text>
                  {item.detail && (
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 12,
                        color: EMBER.colors.textFaint,
                        marginTop: 2,
                      }}
                    >
                      {item.detail}
                    </Text>
                  )}
                </View>
              </Pressable>
            );
          })}
        </View>

        {/* Empty state */}
        {filtered.length === 0 && (
          <SearchEmptyState
            title={t("learn.encyclopedia_screens.tests.empty")}
            onReset={() => {
              setSearch("");
              setActiveCategory(null);
            }}
          />
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
